/**
 * 权限工具函数
 * 用于存储、管理和检查用户的菜单和按钮权限
 */

import type {
  ButtonResultItem,
  PermissionResultItem,
} from '@/pages-pool-member/system/role/services/api';

// LocalStorage 键名
const PERMISSION_STORAGE_KEY = 'langtan-permissions';
const MEMBER_PERMISSION_PREFIX = 'merber:';

/**
 * 生成权限码候选列表：
 * - 保留原始 code
 * - 自动补全 merber: 前缀
 * - 若已带 merber:，同时提供去前缀的回退值（兼容旧数据）
 * - project:basic / project:system 与历史 member:space / member:system 互认
 * - project:facility 与历史 project:basic / member:space（设施叶子）互认
 * - project:member 与历史 member:member（会员中心）互认
 */
function buildPermissionCodeCandidates(code: string): string[] {
  if (!code) return [];

  const aliases = new Set<string>();
  const pushAlias = (c: string) => {
    if (!c) return;
    aliases.add(c);
    if (c.startsWith('project:facility')) {
      aliases.add(c.replace(/^project:facility/, 'project:basic'));
      aliases.add(c.replace(/^project:facility/, 'member:space'));
    }
    if (c.startsWith('project:basic')) {
      aliases.add(c.replace(/^project:basic/, 'member:space'));
      // 设施叶子曾挂在 basic 下，现迁至 facility
      if (/^project:basic:(pool|parking|gate|bandTerminal|band)(:|$)/.test(c)) {
        aliases.add(c.replace(/^project:basic/, 'project:facility'));
      }
    }
    if (c.startsWith('member:space')) {
      aliases.add(c.replace(/^member:space/, 'project:basic'));
      if (/^member:space:(pool|parking|gate|bandTerminal|band)(:|$)/.test(c)) {
        aliases.add(c.replace(/^member:space/, 'project:facility'));
      }
    }
    if (c.startsWith('project:system')) {
      aliases.add(c.replace(/^project:system/, 'member:system'));
      // App版本管理曾挂在 system，现迁至 device
      if (c.startsWith('project:system:appVersions')) {
        aliases.add(
          c.replace(
            /^project:system:appVersions/,
            'project:device:appVersions',
          ),
        );
      }
    }
    if (c.startsWith('member:system')) {
      aliases.add(c.replace(/^member:system/, 'project:system'));
    }
    if (c === 'member:member' || c.startsWith('member:member:')) {
      aliases.add(c.replace(/^member:member/, 'project:member'));
    }
    if (c === 'project:member' || c.startsWith('project:member:')) {
      aliases.add(c.replace(/^project:member/, 'member:member'));
    }
    if (c.startsWith('project:device:appVersions')) {
      aliases.add(
        c.replace(/^project:device:appVersions/, 'project:system:appVersions'),
      );
      aliases.add(
        c.replace(/^project:device:appVersions/, 'energy:platform:appVersions'),
      );
    }
    if (c.startsWith('energy:platform:appVersions')) {
      aliases.add(
        c.replace(/^energy:platform:appVersions/, 'project:device:appVersions'),
      );
      aliases.add(
        c.replace(/^energy:platform:appVersions/, 'project:system:appVersions'),
      );
    }
  };

  if (code.startsWith(MEMBER_PERMISSION_PREFIX)) {
    const withoutPrefix = code.slice(MEMBER_PERMISSION_PREFIX.length);
    pushAlias(code);
    if (withoutPrefix) pushAlias(withoutPrefix);
  } else {
    pushAlias(code);
    pushAlias(`${MEMBER_PERMISSION_PREFIX}${code}`);
  }

  return [...aliases];
}

/** 去掉 Web 端统一使用的 `member:` 前缀，与库中菜单/按钮码（如 `operating:banner`）对齐；兼容历史拼写 `merber:` */
function stripMemberLikePrefix(code: string): string {
  if (code.startsWith('member:')) return code.slice('member:'.length);
  if (code.startsWith(MEMBER_PERMISSION_PREFIX)) {
    return code.slice(MEMBER_PERMISSION_PREFIX.length);
  }
  return code;
}

/** 库中曾误用分号作为末段分隔符，如 `operating:banner;add`，与前端 `operating:banner:add` 对齐 */
function normalizePermissionSeparators(code: string): string {
  return (code || '').replace(/;/g, ':').replace(/:\s+/g, ':').trim();
}

/**
 * 查找按钮列表时用的菜单 code 候选（含去掉 `member:` 后的 `operating:banner` 等）
 */
function buildMenuCodeForButtonLookup(menuCode: string): string[] {
  const base = buildPermissionCodeCandidates(menuCode);
  const out = new Set<string>([...base]);
  const stripped = stripMemberLikePrefix(menuCode);
  if (stripped && stripped !== menuCode) {
    out.add(stripped);
    for (const c of buildPermissionCodeCandidates(stripped)) {
      out.add(c);
    }
  }
  return [...out];
}

/**
 * 与后端返回的 `buttonCode` 比对时的候选（含分号修正、去掉 `member:`）
 */
function buildButtonCodeCandidatesForMatch(buttonCode: string): string[] {
  const base = buildPermissionCodeCandidates(buttonCode);
  const normalizedSep = base.map((c) => normalizePermissionSeparators(c));
  const out = new Set<string>([...base, ...normalizedSep]);
  for (const c of [...base, ...normalizedSep]) {
    const stripped = stripMemberLikePrefix(c);
    if (stripped && stripped !== c) {
      out.add(stripped);
      out.add(normalizePermissionSeparators(stripped));
    }
  }
  return [...out];
}

interface PermissionItem {
  id: number;
  code: string;
  name: string;
  type: 'MODULE' | 'MENU' | 'BUTTON';
  parentId?: number;
  children?: PermissionItem[];
}

/**
 * 菜单项接口（用于动态路由）
 */
export interface MenuPermissionItem {
  id: number;
  parentId: number;
  name: string;
  code: string;
  type: string; // MODULE | MENU | BUTTON
  path: string;
  component: string;
  icon: string;
  sortOrder: number;
  isHidden: number;
  description?: string;
  status: number;
}

/**
 * 完整权限数据结构（包含菜单和按钮）
 */
export interface PermissionData {
  menus: MenuPermissionItem[];
  buttons: Record<string, ButtonResultItem[]>; // key 为 permissionCode
  timestamp: number; // 缓存时间戳
}

/**
 * 扁平化权限树
 */
export const flattenPermissions = (
  permissions: PermissionItem[],
): PermissionItem[] => {
  const result: PermissionItem[] = [];
  const traverse = (items: PermissionItem[]) => {
    items.forEach((item) => {
      result.push(item);
      if (item.children) {
        traverse(item.children);
      }
    });
  };
  traverse(permissions);
  return result;
};

/**
 * 根据权限码查找权限项
 */
export const findPermissionByCode = (
  permissions: PermissionItem[],
  code: string,
): PermissionItem | undefined => {
  const flatPermissions = flattenPermissions(permissions);
  return flatPermissions.find((perm) => perm.code === code);
};

/**
 * 检查是否有指定权限
 */
export const hasPermissionByCode = (
  permissions: PermissionItem[],
  code: string,
): boolean => {
  return !!findPermissionByCode(permissions, code);
};

/**
 * 检查是否有按钮权限
 */
export const hasButtonPermission = (
  permissions: PermissionItem[],
  menuCode: string,
  buttonCode: string,
): boolean => {
  const flatPermissions = flattenPermissions(permissions);
  const menuCodeCandidates = buildPermissionCodeCandidates(menuCode);
  const buttonCodeCandidates = buildPermissionCodeCandidates(buttonCode);

  // 查找菜单
  const menu = flatPermissions.find(
    (perm) =>
      menuCodeCandidates.includes(perm.code) &&
      (perm.type === 'MENU' || perm.type === 'MODULE'),
  );

  if (!menu) return false;

  // 查找该菜单下的按钮权限
  return flatPermissions.some(
    (perm) =>
      perm.parentId === menu.id &&
      perm.type === 'BUTTON' &&
      buttonCodeCandidates.includes(perm.code),
  );
};

/**
 * 检查是否有菜单权限
 */
export const hasMenuPermission = (
  permissions: PermissionItem[],
  menuCode: string,
): boolean => {
  const flatPermissions = flattenPermissions(permissions);
  const menuCodeCandidates = buildPermissionCodeCandidates(menuCode);
  return flatPermissions.some(
    (perm) =>
      menuCodeCandidates.includes(perm.code) &&
      (perm.type === 'MENU' || perm.type === 'MODULE'),
  );
};

/**
 * 获取用户所有按钮权限码
 */
export const getUserButtonPermissions = (
  permissions: PermissionItem[],
): string[] => {
  const flatPermissions = flattenPermissions(permissions);
  return flatPermissions
    .filter((perm) => perm.type === 'BUTTON')
    .map((perm) => perm.code);
};

/**
 * 获取用户所有菜单权限码
 */
export const getUserMenuPermissions = (
  permissions: PermissionItem[],
): string[] => {
  const flatPermissions = flattenPermissions(permissions);
  return flatPermissions
    .filter(
      (perm) =>
        perm.type === 'MENU' ||
        perm.type === 'MODULE' ||
        perm.type === 'SUBSYSTEM',
    )
    .map((perm) => perm.code);
};

// ==================== 新增权限缓存和管理功能 ====================

/**
 * 存储权限数据到 LocalStorage
 * @param permissions 从后端接口获取的权限数据
 */
export function savePermissionsToStorage(
  permissions: PermissionResultItem[],
): void {
  try {
    const menus: MenuPermissionItem[] = [];
    const buttons: Record<string, ButtonResultItem[]> = {};

    // 解析权限数据
    permissions.forEach((item) => {
      const menu = item.swimPermissionItem;
      menus.push(menu);

      // 将按钮权限按照菜单 code 进行分组
      if (item.buttonList && item.buttonList.length > 0) {
        buttons[menu.code] = item.buttonList;
      }
    });

    const permissionData: PermissionData = {
      menus,
      buttons,
      timestamp: Date.now(),
    };

    localStorage.setItem(
      PERMISSION_STORAGE_KEY,
      JSON.stringify(permissionData),
    );
  } catch (error) {
    console.error('保存权限数据失败:', error);
  }
}

/**
 * 从 LocalStorage 获取权限数据
 * @returns 权限数据对象，如果不存在则返回 null
 */
export function getPermissionsFromStorage(): PermissionData | null {
  try {
    const data = localStorage.getItem(PERMISSION_STORAGE_KEY);
    if (!data) {
      return null;
    }
    return JSON.parse(data) as PermissionData;
  } catch (error) {
    console.error('获取权限数据失败:', error);
    return null;
  }
}

/**
 * 清除权限数据
 */
export function clearPermissionsFromStorage(): void {
  try {
    localStorage.removeItem(PERMISSION_STORAGE_KEY);
  } catch (error) {
    console.error('清除权限数据失败:', error);
  }
}

/**
 * 检查是否有指定菜单的访问权限（基于缓存）
 * @param menuCode 菜单编码
 * @returns 是否有权限
 */
export function checkMenuPermission(menuCode: string): boolean {
  const permissions = getPermissionsFromStorage();
  if (!permissions) {
    return false;
  }

  const menuCodeCandidates = buildMenuCodeForButtonLookup(menuCode);
  return permissions.menus.some((menu) =>
    menuCodeCandidates.includes(menu.code),
  );
}

/**
 * 检查是否有指定按钮的权限（基于缓存）
 * @param menuCode 菜单编码
 * @param buttonCode 按钮编码
 * @returns 是否有权限
 */
export function checkButtonPermission(
  menuCode: string,
  buttonCode: string,
): boolean {
  const permissions = getPermissionsFromStorage();
  if (!permissions) {
    return false;
  }

  const menuCodeCandidates = buildMenuCodeForButtonLookup(menuCode);
  const buttonCodeCandidates = buildButtonCodeCandidatesForMatch(buttonCode);

  for (const menuCodeCandidate of menuCodeCandidates) {
    const menuButtons = permissions.buttons[menuCodeCandidate];
    if (!menuButtons || menuButtons.length === 0) {
      continue;
    }

    const hasButton = menuButtons.some((button) => {
      const raw = button.buttonCode || '';
      const stored = normalizePermissionSeparators(raw);
      return buttonCodeCandidates.some(
        (cand) =>
          cand === raw ||
          cand === stored ||
          normalizePermissionSeparators(cand) === stored,
      );
    });
    if (hasButton) {
      return true;
    }
  }
  return false;
}

/**
 * 获取指定菜单的所有按钮权限
 * @param menuCode 菜单编码
 * @returns 按钮权限数组
 */
export function getMenuButtonsFromStorage(
  menuCode: string,
): ButtonResultItem[] {
  const permissions = getPermissionsFromStorage();
  if (!permissions) {
    return [];
  }

  const menuCodeCandidates = buildMenuCodeForButtonLookup(menuCode);
  for (const menuCodeCandidate of menuCodeCandidates) {
    const buttons = permissions.buttons[menuCodeCandidate];
    if (buttons && buttons.length > 0) {
      return buttons;
    }
  }
  return [];
}

/**
 * 获取所有有权限访问的菜单
 * @returns 菜单权限数组
 */
export function getAllMenusFromStorage(): MenuPermissionItem[] {
  const permissions = getPermissionsFromStorage();
  if (!permissions) {
    return [];
  }

  return permissions.menus;
}

/**
 * 构建菜单树结构
 * @param menus 扁平化的菜单数组
 * @returns 树形结构的菜单数组
 */
export function buildMenuTree(
  menus: MenuPermissionItem[],
): MenuPermissionItem[] {
  const menuMap = new Map<
    number,
    MenuPermissionItem & { children?: MenuPermissionItem[] }
  >();
  const tree: (MenuPermissionItem & { children?: MenuPermissionItem[] })[] = [];

  // 创建映射表
  menus.forEach((menu) => {
    menuMap.set(menu.id, { ...menu, children: [] });
  });

  // 构建树形结构
  menus.forEach((menu) => {
    const menuNode = menuMap.get(menu.id);
    if (!menuNode) return;

    if (menu.parentId === 0 || !menuMap.has(menu.parentId)) {
      // 根节点
      tree.push(menuNode);
    } else {
      // 子节点
      const parent = menuMap.get(menu.parentId);
      if (parent && parent.children) {
        parent.children.push(menuNode);
      }
    }
  });

  // 按 sortOrder 排序，并清理空的 children 数组（但保留有子节点的父级）
  const sortMenus = (menus: any[]): any[] => {
    return menus
      .filter((menu) => {
        if (menu.status == null || menu.status === '') return true;
        return Number(menu.status) === 1;
      })
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((menu) => {
        if (menu.children && menu.children.length > 0) {
          menu.children = sortMenus(menu.children);
        } else {
          delete menu.children;
        }
        return menu;
      });
  };

  return sortMenus(tree);
}

/**
 * 将菜单权限转换为 UmiJS 路由配置
 * @param menus 菜单权限数组
 * @returns UmiJS 路由配置
 */
export function convertMenusToRoutes(menus: MenuPermissionItem[]): any[] {
  return menus
    .filter((menu) => menu.status === 1) // 只过滤启用的菜单，保留隐藏菜单以注册路由
    .map((menu) => ({
      path: menu.path,
      name: menu.name,
      icon: menu.icon,
      component: menu.component,
      hideInMenu: menu.isHidden === 1, // 设置是否在左侧菜单中隐藏
      access: menu.code, // 使用菜单 code 作为 access 权限标识
    }));
}
